<template>
  <div>
    <!-- Category pills + search -->
    <div class="d-flex flex-wrap align-center mb-3">
      <div class="mr-auto" role="group" aria-label="Filter by category">
        <v-btn
          v-for="c in categories"
          :key="c"
          small rounded depressed
          class="mr-2 mb-2 text-capitalize kvm-pick__pill"
          :class="{ 'kvm-pick__pill--active': c === activeCategory }"
          :color="c === activeCategory ? 'primary' : ''"
          :text="c !== activeCategory"
          :aria-pressed="c === activeCategory ? 'true' : 'false'"
          @click="activeCategory = c"
        >{{ c }}</v-btn>
      </div>
      <v-text-field
        v-model="search"
        prepend-inner-icon="mdi-magnify"
        placeholder="Search"
        aria-label="Search operating systems and apps"
        outlined dense hide-details clearable
        style="max-width: 240px;"
      />
    </div>

    <!-- Card grid -->
    <v-row dense role="group" aria-label="Operating system and application catalog">
      <v-col v-for="item in filteredItems" :key="item.kind + ':' + item.slug" cols="12" sm="6" md="4" lg="3">
        <v-card
          outlined
          class="vds-pick-card"
          :style="isSelected(item) ? selectedStyle : null"
          role="button"
          tabindex="0"
          aria-haspopup="dialog"
          :aria-pressed="isSelected(item) ? 'true' : 'false'"
          :aria-label="cardAriaLabel(item)"
          @click="openDetail(item)"
          @keydown.enter.prevent="openDetail(item)"
          @keydown.space.prevent="openDetail(item)"
        >
          <div class="d-flex align-center pa-3">
            <kvm-logo :item="item" :size="28" :default-icon="defaultIcon(item)" class="mr-3" />
            <span class="body-2 text-truncate flex-grow-1" :title="item.name">{{ item.name }}</span>
            <v-icon v-if="isSelected(item)" small color="primary" class="ml-1" aria-hidden="true">mdi-check-circle</v-icon>
            <v-icon v-else-if="item.popular" small color="amber darken-2" class="ml-1" aria-hidden="true">mdi-star</v-icon>
          </div>
        </v-card>
      </v-col>
      <v-col v-if="!filteredItems.length" cols="12">
        <p class="text-center py-6 mb-0 kvm-pick__empty">No matches.</p>
      </v-col>
    </v-row>

    <!-- Current selection summary -->
    <v-alert v-if="value && selectedItem" dense text color="primary" class="mt-2 mb-0 kvm-pick__summary" aria-live="polite">
      <div class="d-flex align-center">
        <span class="mr-auto">Selected: <strong>{{ selectedItem.name }}</strong>{{ selectedOptionsText }}</span>
        <gg-btn x-small text class="kvm-pick__btn" :aria-label="'Change options for ' + selectedItem.name" @click="openDetail(selectedItem)">Change</gg-btn>
      </div>
    </v-alert>

    <!-- Detail / confirm dialog -->
    <v-dialog v-model="dialog" max-width="560" scrollable>
      <gg-card v-if="active" role="dialog" aria-modal="true" aria-labelledby="kvm-pick-dialog-title">
        <gg-card-text class="pa-4">
          <div class="d-flex align-start mb-3">
            <kvm-logo :item="active" :size="40" :default-icon="defaultIcon(active)" class="mr-3" />
            <div class="flex-grow-1">
              <div id="kvm-pick-dialog-title" class="body-1 font-weight-medium">{{ active.name }}</div>
              <div v-if="active.subtitle" class="text-caption kvm-pick__subtitle">{{ active.subtitle }}</div>
            </div>
            <v-chip v-if="active.popular" x-small color="grey lighten-3" class="ml-2 mt-1">
              <v-icon x-small left color="amber darken-2" aria-hidden="true">mdi-star</v-icon>Popular
            </v-chip>
            <v-btn icon small class="ml-1 kvm-pick__btn" aria-label="Close" @click="dialog = false"><v-icon small aria-hidden="true">mdi-close</v-icon></v-btn>
          </div>

          <div v-if="renderedDescription" class="vds-pick-desc body-2 mb-3" v-html="renderedDescription" />

          <div v-if="(active.links || []).length" class="mb-4">
            <a
              v-for="(l, i) in active.links"
              :key="i"
              :href="l.url"
              target="_blank" rel="noopener noreferrer"
              class="mr-4 text-caption kvm-pick__link"
            >{{ l.label }} <v-icon x-small aria-hidden="true">mdi-open-in-new</v-icon><span class="kvm-sr-only"> (opens in a new tab)</span></a>
          </div>

          <div v-for="opt in (active.options || [])" :key="opt.key" class="mb-3">
            <!-- select (default when no type given) -->
            <v-select
              v-if="optType(opt) === 'select'"
              v-model="optionValues[opt.key]"
              :items="opt.choices"
              item-text="label"
              item-value="value"
              :label="opt.label"
              outlined dense hide-details
            />

            <!-- radio -->
            <div v-else-if="optType(opt) === 'radio'">
              <div class="subtitle-2 mb-1">{{ opt.label }}</div>
              <v-radio-group v-model="optionValues[opt.key]" dense hide-details class="mt-0" :aria-label="opt.label">
                <v-radio v-for="c in (opt.choices || [])" :key="c.value" :label="c.label" :value="c.value" />
              </v-radio-group>
            </div>

            <!-- checkbox -->
            <v-checkbox
              v-else-if="optType(opt) === 'checkbox'"
              v-model="optionValues[opt.key]"
              :label="opt.label"
              dense hide-details class="mt-0"
            />

            <!-- text -->
            <v-text-field
              v-else-if="optType(opt) === 'text'"
              v-model="optionValues[opt.key]"
              :label="opt.label"
              outlined dense hide-details="auto"
              :counter="opt.max || undefined"
              :rules="textRules(opt)"
            />

            <!-- password (secret — never prefilled) -->
            <v-text-field
              v-else-if="optType(opt) === 'password'"
              v-model="optionValues[opt.key]"
              :label="opt.label"
              outlined dense hide-details="auto"
              :type="revealOpt[opt.key] ? 'text' : 'password'"
              :counter="opt.max || undefined"
              :rules="textRules(opt)"
              autocomplete="new-password"
            >
              <template v-slot:append>
                <v-btn
                  icon x-small class="kvm-pick__reveal"
                  :aria-label="(revealOpt[opt.key] ? 'Hide ' : 'Show ') + opt.label"
                  :aria-pressed="revealOpt[opt.key] ? 'true' : 'false'"
                  @click="$set(revealOpt, opt.key, !revealOpt[opt.key])"
                >
                  <v-icon small aria-hidden="true">{{ revealOpt[opt.key] ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                </v-btn>
              </template>
            </v-text-field>

            <!-- password_generate (a NEW secret — offer a generator) -->
            <div v-else-if="optType(opt) === 'password_generate'">
              <v-text-field
                v-model="optionValues[opt.key]"
                :label="opt.label"
                outlined dense hide-details="auto"
                :type="revealOpt[opt.key] ? 'text' : 'password'"
                :counter="opt.max || undefined"
                :rules="textRules(opt)"
                autocomplete="new-password"
              >
                <template v-slot:append>
                  <v-btn
                    icon x-small class="kvm-pick__reveal"
                    :aria-label="(revealOpt[opt.key] ? 'Hide ' : 'Show ') + opt.label"
                    :aria-pressed="revealOpt[opt.key] ? 'true' : 'false'"
                    @click="$set(revealOpt, opt.key, !revealOpt[opt.key])"
                  >
                    <v-icon small aria-hidden="true">{{ revealOpt[opt.key] ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
              <gg-btn text small color="primary" class="px-1 mt-1 kvm-pick__btn" :aria-label="'Generate a strong password for ' + opt.label" @click="generatePassword(opt)">
                <v-icon left small aria-hidden="true">mdi-auto-fix</v-icon>Generate
              </gg-btn>
            </div>
          </div>

          <div class="text-right mt-2">
            <gg-btn text class="mr-2 kvm-pick__btn" @click="dialog = false">Cancel</gg-btn>
            <gg-btn tile color="primary" class="kvm-pick__btn kvm-pick__confirm" :disabled="!optionsValid" @click="confirm">{{ confirmLabel }}</gg-btn>
          </div>
        </gg-card-text>
      </gg-card>
    </v-dialog>
  </div>
</template>

<script>
import { renderMarkdown } from '@/helpers/markdown'
import KvmLogo from '@/views/services/hosting/kvm/KvmLogo'

export default {
  name: 'KvmCatalogPicker',
  components: { KvmLogo },
  props: {
    operatingSystems: { type: Array, default: () => [] },
    apps: { type: Array, default: () => [] },
    // v-model: the chosen selection { kind: 'os'|'app', slug, options } or null.
    value: { type: Object, default: null },
    confirmLabel: { type: String, default: 'Select' },
  },
  data: () => ({
    activeCategory: '',
    search: '',
    dialog: false,
    active: null,
    optionValues: {},
    revealOpt: {}, // per-password-option reveal toggles
  }),
  computed: {
    primaryColor () {
      return (this.$vuetify.theme.currentTheme && this.$vuetify.theme.currentTheme.primary) || '#1976d2'
    },
    selectedStyle () {
      return { borderColor: this.primaryColor, boxShadow: `inset 0 0 0 1px ${this.primaryColor}` }
    },
    // Merge OS + app catalogs into one list, tagging kind and a display category.
    allItems () {
      const os = (this.operatingSystems || []).map(i => ({ ...i, kind: 'os', category: i.category || 'Operating System' }))
      const apps = (this.apps || []).map(i => ({ ...i, kind: 'app', category: i.category || 'Application' }))
      return [...os, ...apps]
    },
    categories () {
      const seen = []
      this.allItems.forEach(i => { if (!seen.includes(i.category)) seen.push(i.category) })
      return seen
    },
    filteredItems () {
      const q = (this.search || '').trim().toLowerCase()
      return this.allItems.filter(i =>
        i.category === this.activeCategory &&
        (!q || (i.name || '').toLowerCase().includes(q)))
    },
    selectedItem () {
      if (!this.value) return null
      return this.allItems.find(i => i.kind === this.value.kind && i.slug === this.value.slug) || null
    },
    // Human-readable summary of the chosen options — choice labels where possible,
    // checkboxes only when on, and never the value of a secret (password) option.
    selectedOptionsText () {
      const sel = this.selectedItem
      const opts = this.value && this.value.options
      if (!sel || !opts) return ''
      const parts = []
      ;(sel.options || []).forEach(o => {
        const type = this.optType(o)
        if (type === 'password' || type === 'password_generate') return
        const v = opts[o.key]
        if (v == null || v === '') return
        if (type === 'checkbox') { if (v) parts.push(o.label); return }
        if ((type === 'select' || type === 'radio') && o.choices) {
          const c = o.choices.find(ch => ch.value === v)
          parts.push(c ? c.label : v)
          return
        }
        parts.push(String(v))
      })
      return parts.length ? ` (${parts.join(', ')})` : ''
    },
    renderedDescription () {
      return renderMarkdown(this.active && this.active.description)
    },
    optionsValid () {
      if (!this.active) return false
      return (this.active.options || []).every(o => {
        const type = this.optType(o)
        if (type === 'checkbox') return true // a boolean is always valid
        const v = this.optionValues[o.key]
        if (o.required && (v == null || v === '')) return false
        if ((type === 'text' || type === 'password' || type === 'password_generate') && v) {
          if (o.min != null && String(v).length < o.min) return false
          if (o.max != null && String(v).length > o.max) return false
        }
        return true
      })
    },
  },
  watch: {
    categories: {
      immediate: true,
      handler (cats) {
        if (cats.length && !cats.includes(this.activeCategory)) this.activeCategory = cats[0]
      },
    },
  },
  methods: {
    isSelected (item) {
      return !!this.value && this.value.kind === item.kind && this.value.slug === item.slug
    },
    // Accessible name for a catalog tile (the inner icons/badges are aria-hidden,
    // so kind/popularity must be conveyed in text; "selected" comes via aria-pressed).
    cardAriaLabel (item) {
      const kind = item.kind === 'os' ? 'operating system' : 'application'
      return `${item.name}, ${kind}${item.popular ? ', popular' : ''}`
    },
    // Shown (neutral) only when the item has no logo.
    defaultIcon (item) {
      return item.kind === 'os' ? 'mdi-harddisk' : 'mdi-application'
    },
    // Effective field type — defaults to 'select' when the catalog omits `type`.
    optType (opt) {
      return opt.type || 'select'
    },
    // Length validation for text/password fields (min/max are optional).
    textRules (opt) {
      const rules = []
      if (opt.required) rules.push(v => (v != null && v !== '') || 'Required.')
      if (opt.min != null) rules.push(v => (!v || String(v).length >= opt.min) || `At least ${opt.min} characters.`)
      if (opt.max != null) rules.push(v => (!v || String(v).length <= opt.max) || `At most ${opt.max} characters.`)
      return rules
    },
    // Fill a password_generate field with a strong random secret (within min/max).
    generatePassword (opt) {
      const charset = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*-_=+'
      let len = 20
      if (opt.min != null) len = Math.max(len, Number(opt.min))
      if (opt.max != null) len = Math.min(len, Number(opt.max))
      if (!(len >= 1)) len = 16
      const arr = new Uint32Array(len)
      window.crypto.getRandomValues(arr)
      let out = ''
      for (let i = 0; i < len; i++) out += charset[arr[i] % charset.length]
      this.$set(this.optionValues, opt.key, out)
      this.$set(this.revealOpt, opt.key, true) // reveal so the customer can note it
    },
    openDetail (item) {
      this.active = item
      const prev = this.value && this.value.kind === item.kind && this.value.slug === item.slug
        ? (this.value.options || {})
        : {}
      const opts = {}
      ;(item.options || []).forEach(o => {
        const type = this.optType(o)
        // Secrets (existing or to-be-generated) are never prefilled.
        if (type === 'password' || type === 'password_generate') { opts[o.key] = ''; return }
        if (prev[o.key] != null) { opts[o.key] = prev[o.key]; return }
        if (type === 'checkbox') { opts[o.key] = !!o.default; return }
        if (o.default != null) { opts[o.key] = o.default; return }
        if ((type === 'select' || type === 'radio') && o.choices && o.choices.length) {
          opts[o.key] = o.choices[0].value; return
        }
        opts[o.key] = type === 'text' ? '' : null
      })
      this.optionValues = opts
      this.revealOpt = {}
      this.dialog = true
    },
    confirm () {
      this.$emit('input', {
        kind: this.active.kind,
        slug: this.active.slug,
        options: { ...this.optionValues },
      })
      this.dialog = false
    },
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.vds-pick-card {
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &:focus-visible {
    @include kvm-focus-ring;
  }

  @include kvm-reduced-motion {
    transition: none;
  }
}

.kvm-pick__pill:focus-visible,
.kvm-pick__reveal:focus-visible,
.kvm-pick__btn:focus-visible {
  @include kvm-focus-ring;
}

// Active (filled) category pill: deepen the brand green so white text reaches AA
// (white on #0a7c3c = 5.3:1; the default brand #0ca750 fails at 3.15:1).
.kvm-pick__pill--active {
  @include kvm-btn-primary-aa;
}

// Dialog confirm (filled primary) button -> AA-contrast white-on-deep-green.
.kvm-pick__confirm {
  @include kvm-btn-primary-aa;
}

// Keep the dense in-field show/hide control at a usable pointer target.
.kvm-pick__reveal {
  @include kvm-min-target(24px);
}

.kvm-pick__empty,
.kvm-pick__subtitle {
  @include kvm-secondary-text;
}

.kvm-sr-only {
  @include kvm-sr-only;
}

// The "Selected: …" summary uses Vuetify's faint primary `text` alert; lift its
// body to AA contrast on the pale tint (the wording carries the meaning, not hue).
.kvm-pick__summary ::v-deep .v-alert__content {
  color: rgba(0, 0, 0, 0.87);
}
.theme--dark .kvm-pick__summary ::v-deep .v-alert__content {
  color: rgba(255, 255, 255, 0.92);
}

// Rendered markdown description: keep paragraph rhythm and give links an
// AA-contrast, on-brand colour + a visible focus ring in both themes.
.vds-pick-desc ::v-deep p {
  margin-bottom: 8px;
}
.vds-pick-desc ::v-deep a {
  color: $kvm-focus-light;
  word-break: break-all;

  &:focus-visible {
    @include kvm-focus-ring;
  }
}
.theme--dark .vds-pick-desc ::v-deep a {
  color: $kvm-focus-dark;
}

.kvm-pick__link {
  color: $kvm-focus-light;

  &:focus-visible {
    @include kvm-focus-ring;
  }
}
.theme--dark .kvm-pick__link {
  color: $kvm-focus-dark;
}
</style>
